// react
import { useEffect, useMemo, useState } from 'react';
import { Filters } from 'react-table';

// third-party
import { Button, Stack } from '@mui/material';

// project import
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { deleteCalendarLogs, fetchCalendarDetails, fetchCalendarHistoryList } from 'pages/calendar/api';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import CalendarHistoryTableColumnConfig, {
    DefaultCalendarHistoryTableColumnIdToSortBy,
    DefaultCalendarHistoryTableSortingType,
    DefaultVisibleCalendarHistoryColumns,
    SearchableCalendarHistoryTableColumnConfig,
} from './event-history-column-config';
import { EventLogDto } from 'common/dto-types/event-dto';
import { useParams } from 'react-router';
import { dispatch } from 'store';
import { provideAlternateTitle } from 'store/reducers/menu';
import CalendarDto from 'common/dto-types/calendar-dto';
import ConfirmDialog from 'components/ConfirmDialog';
import { openSnackbar } from 'store/reducers/snackbar';

const CalendarEventHistoryPage = () => {
    const [eventLogsList, setEventLogsList] = useState<Array<EventLogDto>>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const defaultVisibleColumnConfig = useMemo(() => DefaultVisibleCalendarHistoryColumns, []);
    const defaultColumnConfig = useMemo(() => CalendarHistoryTableColumnConfig, []);
    const [confirmLogDeletionOpen, setConfirmLogDeletionOpen] = useState(false);

    const { id, eventId } = useParams();

    const calendarId = useMemo(() => {
        return id ? parseInt(id) : 0;
    }, [id]);

    const defaultHistoryFilters: Filters<any> = useMemo(() => {
        if (eventId === undefined) return [];
        else return [{ id: 'eventId', value: eventId }];
    }, [eventId]);

    const fetchData: BaseTableFetchFuncProps = async ({
        pageSize,
        pageIndex,
        sortingType,
        sortBy,
        filters,
        searchTerm,
    }) => {
        const data = await fetchCalendarHistoryList(
            {
                pageIndex,
                pageSize,
                sortingType,
                sortBy,
                andSpecifications: mapFiltersToSpecification(filters),
                orSpecifications:
                    searchTerm && searchTerm.length > 0
                        ? mapSearchParamToSpecification(searchTerm, SearchableCalendarHistoryTableColumnConfig)
                        : undefined,
            },
            calendarId,
        );

        if (data) {
            setEventLogsList(data.elements);
            setPageCount(Math.ceil(data.total / pageSize));
            setTotalItems(data.total);
        }
    };

    useEffect(() => {
        (() => {
            fetchCalendarDetails(calendarId).then((result: CalendarDto | null) => {
                if (result) {
                    dispatch(provideAlternateTitle(result.localizedContents?.en?.shortName ?? undefined));
                }
            });
        })();
    }, [calendarId]);

    const handleLogClearing = async () => {
        const data = await deleteCalendarLogs(calendarId);
        if (data) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Logs deleted successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            await fetchData({
                pageSize: 10,
                pageIndex: 0,
                sortingType: DefaultCalendarHistoryTableSortingType,
                sortBy: DefaultCalendarHistoryTableColumnIdToSortBy,
                filters: [],
                searchTerm: '',
            });
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Failed to delete logs',
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }

        setConfirmLogDeletionOpen(false);
    };

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Stack direction="row" justifyContent="flex-end" marginTop={2} marginRight={2}>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            setConfirmLogDeletionOpen(true);
                        }}
                    >
                        Clear Logs
                    </Button>
                </Stack>
                <ScrollX>
                    <BaseTable
                        data={eventLogsList}
                        fetchData={fetchData}
                        pageCount={pageCount}
                        defaultColumnIdToSortBy={DefaultCalendarHistoryTableColumnIdToSortBy}
                        defaultSortingType={DefaultCalendarHistoryTableSortingType}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        defaultFilters={defaultHistoryFilters}
                    />
                </ScrollX>
                {confirmLogDeletionOpen && (
                    <ConfirmDialog
                        open={confirmLogDeletionOpen}
                        title="Are you sure you want to clear this Calendar's logs?"
                        content={`This will permamently delete all Logs under this Calendar. Currently this amounts to: ${totalItems} logs.`}
                        confirmButtonText="Clear Logs"
                        onClose={() => {
                            setConfirmLogDeletionOpen(false);
                        }}
                        handleCancel={() => {
                            setConfirmLogDeletionOpen(false);
                        }}
                        handleConfirm={handleLogClearing}
                    />
                )}
            </Stack>
        </MainCard>
    );
};

export default CalendarEventHistoryPage;
